import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ImageIcon, Link2, RotateCcw, Loader2, Check, Smartphone, Palette } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ImageUploader from "@/components/ImageUploader";
import { useBranding } from "@/hooks/useBranding";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function BrandingIdentityCard({ branding }) {
  const [name, setName] = useState(branding.short_name || "FUTURO PDV");
  const [theme, setTheme] = useState(branding.theme_color || "#2563eb");
  const [bg, setBg] = useState(branding.background_color || "#ffffff");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setName(branding.short_name || "FUTURO PDV");
    setTheme(branding.theme_color || "#2563eb");
    setBg(branding.background_color || "#ffffff");
  }, [branding.short_name, branding.theme_color, branding.background_color]);
  const dirty = name !== (branding.short_name || "FUTURO PDV") || theme !== (branding.theme_color || "#2563eb") || bg !== (branding.background_color || "#ffffff");
  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/branding/settings`, { short_name: name.trim(), theme_color: theme, background_color: bg });
      await branding.refresh();
      toast.success("Nome e cores do app atualizados — já valem no manifest, barra do navegador e cabeçalho.");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    setBusy(false);
  };
  return (
    <Card className="p-5" data-testid="branding-identity-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ background: theme }}><Palette className="w-5 h-5" /></div>
        <div>
          <h3 className="font-bold">Nome e cor do app</h3>
          <p className="text-xs text-muted-foreground">Nome curto que aparece sob o ícone instalado e no cabeçalho; cor do tema (barra do navegador/splash) e cor de fundo do splash.</p>
        </div>
      </div>
      <div className="grid md:grid-cols-[1fr_auto_auto_auto] gap-4 items-end">
        <div>
          <label className="text-xs font-semibold text-muted-foreground">Nome curto (2–24)</label>
          <Input value={name} maxLength={24} onChange={(e) => setName(e.target.value)} data-testid="branding-name-input" />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground">Cor do tema</label>
          <div className="flex items-center gap-2">
            <input type="color" value={theme} onChange={(e) => setTheme(e.target.value)} className="w-10 h-10 rounded-lg border border-border cursor-pointer bg-transparent" data-testid="branding-theme-color" />
            <Input value={theme} onChange={(e) => setTheme(e.target.value)} className="w-28 font-mono" data-testid="branding-theme-hex" />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground">Fundo do splash</label>
          <div className="flex items-center gap-2">
            <input type="color" value={bg} onChange={(e) => setBg(e.target.value)} className="w-10 h-10 rounded-lg border border-border cursor-pointer bg-transparent" data-testid="branding-bg-color" />
            <Input value={bg} onChange={(e) => setBg(e.target.value)} className="w-28 font-mono" data-testid="branding-bg-hex" />
          </div>
        </div>
        <Button onClick={save} disabled={busy || !dirty} data-testid="branding-identity-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Salvar</>}
        </Button>
      </div>
      <div className="mt-4 flex items-center gap-3 p-3 rounded-2xl border border-border w-fit" style={{ background: bg }} data-testid="branding-identity-preview">
        <img src={`${API}/branding/icon/96.png?v=${branding.updated_at || "default"}`} alt="" className="w-12 h-12 rounded-xl" />
        <div>
          <div className="text-sm font-bold" style={{ color: theme }}>{name || "—"}</div>
          <div className="text-[10px] text-muted-foreground">prévia da tela inicial / splash</div>
        </div>
      </div>
    </Card>
  );
}

export default function AdminBrandingTab() {
  const branding = useBranding();
  const [pendingUrl, setPendingUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const apply = async (url) => {
    if (!url) return;
    setBusy(true);
    try {
      await axios.put(`${API}/admin/branding/icon`, { image_url: url });
      await branding.refresh();
      setPendingUrl("");
      toast.success("Ícone do app atualizado! Já vale no PWA, favicon, cabeçalho e push — sem novo deploy.");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao aplicar ícone"); }
    setBusy(false);
  };

  const reset = async () => {
    if (!window.confirm("Restaurar o ícone padrão do FUTURO PDV?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/branding/icon`);
      await branding.refresh();
      toast.success("Ícone padrão restaurado");
    } catch { toast.error("Erro ao restaurar"); }
    setBusy(false);
  };

  const sizes = [512, 192, 96, 64];
  return (
    <div className="space-y-4" data-testid="admin-branding-tab">
      <BrandingIdentityCard branding={branding} />
      <Card className="p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Smartphone className="w-5 h-5" /></div>
          <div>
            <h3 className="font-bold">Ícone do app (PWA)</h3>
            <p className="text-xs text-muted-foreground">Envie uma imagem quadrada (mín. 192×192, ideal 1024×1024, PNG com fundo). Ela substitui o ícone do app instalado, o favicon, o logo do cabeçalho e o ícone das notificações push — aplicado na hora.</p>
          </div>
        </div>

        <div className="grid md:grid-cols-[1fr_auto] gap-6">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <ImageUploader testid="branding-upload-btn" label="Enviar imagem" accept="image/*" onUploaded={(url) => apply(url)} />
              <span className="text-xs text-muted-foreground">ou</span>
              <div className="flex items-center gap-2 flex-1 min-w-[260px]">
                <Link2 className="w-4 h-4 text-muted-foreground" />
                <Input value={pendingUrl} onChange={(e) => setPendingUrl(e.target.value)} placeholder="https://... (URL da imagem)" data-testid="branding-url-input" />
                <Button onClick={() => apply(pendingUrl.trim())} disabled={busy || !pendingUrl.trim()} data-testid="branding-apply-url">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Aplicar</>}
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span data-testid="branding-status">{branding.has_custom ? `Ícone personalizado ativo · atualizado em ${new Date(branding.updated_at).toLocaleString("pt-BR")}` : "Usando o ícone padrão"}</span>
              {branding.has_custom && (
                <Button size="sm" variant="ghost" onClick={reset} disabled={busy} data-testid="branding-reset-btn"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Restaurar padrão</Button>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground">
              Manifest dinâmico: <a href={`${API}/manifest.json`} target="_blank" rel="noreferrer" className="underline" data-testid="branding-manifest-link">/api/manifest.json</a> · Dica: em apps já instalados, o sistema atualiza o ícone na próxima sincronização do navegador.
            </div>
          </div>

          <div className="flex flex-col items-center gap-3" data-testid="branding-preview">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Pré-visualização</div>
            <div className="flex items-end gap-4">
              {sizes.map((s) => (
                <div key={s} className="flex flex-col items-center gap-1">
                  {branding.icon_url ? (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/branding/icon/${s}.png?v=${branding.updated_at || "default"}`}
                      alt={`${s}px`}
                      style={{ width: Math.min(s, 96), height: Math.min(s, 96) }}
                      className="rounded-2xl shadow border border-border object-cover"
                      data-testid={`branding-preview-${s}`}
                    />
                  ) : <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center"><ImageIcon className="w-5 h-5 text-muted-foreground" /></div>}
                  <span className="text-[10px] text-muted-foreground">{s}px</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-2 p-2 rounded-xl bg-muted/50">
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/branding/icon/512.png?maskable=1&v=${branding.updated_at || "default"}`} alt="maskable" className="w-14 h-14 rounded-full object-cover" data-testid="branding-preview-maskable" />
              <div className="text-[10px] text-muted-foreground leading-tight">Versão <b>maskable</b><br />(Android adaptativo)</div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
